import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtCompact, fmtPct } from "../lib/api";
import {
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell as RCell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, LabelList,
} from "recharts";
import {
  ChartLineUp, ShieldCheckered, ShieldCheck, Calculator, ChartLine,
  Receipt, ArrowSquareOut, Buildings, X, Brain, Sparkle, FlowArrow,
  Compass, ChartPie, ChartBar, ChartLine as ChartLineIcon,
  Truck, Package as PackageIcon,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────────────
 * Scorecard · Historical Achievement & Financial Proof
 * ──────────────────────────────────────────────────────────────────────────
 * Board-deck summary of value already booked: hero, financial velocity,
 * lever ROI mix, PDI momentum across categories, and a value attestation
 * waterfall — all backed by the same Mongo Context Ledger traces.
 * ──────────────────────────────────────────────────────────────────────── */

// Executive palette
const EX_GREEN      = "#0D9488";   // hero, achievement
const EX_GREEN_DARK = "#064E3B";   // emphasis
const EX_GREEN_SOFT = "#A7F3D0";   // area fill
const EX_AMBER      = "#B45309";   // baseline / projection
const EX_AMBER_SOFT = "#FDE68A";
const EX_RED        = "#991B1B";
const EX_INK        = "#0F172A";
const EX_SLATE      = "#334155";
const EX_BORDER     = "#D1D5DB";
const EX_CARD_BG    = "#FFFFFF";

const LEVER_COLORS = { SHOULD_COST: EX_GREEN_DARK, RATE_BENCHMARK: EX_AMBER };

// KPI → assertion citation map
const ASSERT = {
  HERO_REALIZED:   ["A11", "A04"],
  GROSS_RATE:      ["A03", "A04"],
  ADJUSTED_RATE:   ["A07", "A08"],
  CAPTURE_RATE:    ["A07", "A08"],
};

export default function ScorecardView({ openProof, openSourcing }) {
  const [ledger, setLedger]       = useState(null);
  const [learning, setLearning]   = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [drawer, setDrawer]       = useState(null);
  // PDI sensitivity threshold — only POs with PDI ≥ threshold count toward the realized view.
  const [pdiThreshold, setPdiThreshold] = useState(0);

  useEffect(() => {
    api.ledger({ limit: 500 }).then(setLedger).catch(() => setLedger([]));
    api.systemLearning(200).then(setLearning).catch(() => setLearning({ traces: [] }));
    api.integrity().then(setIntegrity).catch(() => setIntegrity({ assertions: [], passed: 0, total: 11 }));
  }, []);

  // Filter ledger by PDI threshold for downstream metrics
  const filteredLedger = useMemo(() => {
    if (!ledger) return null;
    if (pdiThreshold <= 0) return ledger;
    return ledger.filter((r) => (100 - Number(r.risk_score || 0)) >= pdiThreshold);
  }, [ledger, pdiThreshold]);

  const m = useMemo(() => computeMetrics(filteredLedger, learning), [filteredLedger, learning]);
  const baselineMetrics = useMemo(() => computeMetrics(ledger, learning), [ledger, learning]); // unfiltered for KPI deltas
  const assertIdx = useMemo(() => {
    const idx = {};
    (integrity?.assertions || []).forEach((a) => { idx[a.id] = a; });
    return idx;
  }, [integrity]);

  if (!ledger || !learning || !integrity) return <Skeleton />;

  // Build a drill-down by ledger row predicate
  const openDrill = (title, predicate, kind = "SAVINGS") => {
    const rows = ledger.filter(predicate).sort((a, b) => (+b.delta || 0) - (+a.delta || 0));
    const tracesByPo = {};
    (learning.traces || []).forEach((t) => { tracesByPo[t.key] = t; });
    const enriched = rows.slice(0, 25).map((row) => {
      const t = tracesByPo[row.po_id];
      return {
        kind: t?.kind || "LEDGER",
        key: row.po_id,
        summary: t?.summary ||
          `${row.po_number} · ${row.supplier} · gross ${fmtUSD(row.delta)} · decision-adj ${fmtUSD(row.risk_adjusted_delta)}`,
        tiers_used: t?.tiers_used || [],
        has_opus_trace: t?.has_opus_trace || false,
        ledger_row: row,
      };
    });
    setDrawer({ title, kind, traces: enriched });
  };

  const openLedgerProof = (kindHint = "SAVINGS") => {
    const traces = (learning.traces || []).filter((t) => t.kind === kindHint);
    const enriched = traces.length > 0
      ? traces.map((t) => ({ ...t, ledger_row: m.poIdx[t.key] || null }))
      : m.topGross.slice(0, 8).map((row) => ({
          kind: "LEDGER", key: row.po_id,
          summary: `${row.po_number} · ${row.supplier} · gross ${fmtUSD(row.delta)} · decision-adj ${fmtUSD(row.risk_adjusted_delta)}`,
          tiers_used: [], has_opus_trace: false, ledger_row: row,
        }));
    setDrawer({ title: "Ledger Reconcile · top exposures available for Proof", kind: kindHint, traces: enriched });
  };

  return (
    <section
      className="bg-[#F9FAFB] border border-[#D1D5DB] rounded-sm"
      data-testid="scorecard-receipt-of-value"
    >
      {/* Header */}
      <header className="px-5 py-4 border-b border-[#D1D5DB] bg-[#F3F4F6] flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-950 flex items-center gap-2 tracking-tight">
            <Receipt weight="duotone" className="text-emerald-800" size={20} />
            Scorecard · Historical Achievement &amp; Financial Proof
          </h2>
          <p className="text-xs text-slate-700 mt-0.5 font-mono font-medium">
            Board-grade view of value already banked · velocity, lever mix, PDI momentum, value attestation
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <HeroPill
            testid="hero-pill-tlc-savings"
            label="Total TLC Savings Identified"
            value={fmtCompact(m.leakage.tlcSavings)}
            sub="leakage recaptured · Direct"
            positive
          />
          <HeroPill
            testid="hero-pill-velocity"
            label="Savings Velocity"
            value={`${m.velocityMoM >= 0 ? "+" : ""}${m.velocityMoM.toFixed(1)}%`}
            sub="month-over-month"
            positive={m.velocityMoM >= 0}
          />
          <HeroPill
            testid="hero-pill-integrity"
            label="CFO Integrity Pulse"
            value={`${integrity.passed}/${integrity.total}`}
            sub="live assertions"
            positive={integrity.passed === integrity.total}
            live
          />
          <SealBadge integrity={integrity} ids={ASSERT.HERO_REALIZED} idx={assertIdx} />
        </div>
      </header>

      {/* ─── Hero · Total Realized Value ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-[#E5E7EB]">
        <div className="lg:col-span-3 bg-white p-5 lg:p-6 relative overflow-hidden">
          <div className="tracking-mono-label text-[10px] text-emerald-800 font-bold">
            Total Realized Value · Decision-Adjusted · TTM
          </div>
          <div
            data-testid="scorecard-hero-value"
            className="font-display font-extrabold leading-none tracking-tighter mt-2 text-4xl sm:text-5xl lg:text-6xl tabular-nums"
            style={{ color: EX_GREEN_DARK }}
          >
            {fmtUSD(m.riskAdjusted)}
          </div>
          <div className="mt-3 flex items-center gap-3 text-[12px] font-mono text-slate-800 flex-wrap font-medium tabular-nums">
            <span>Gross <span className="text-slate-950 font-bold">{fmtCompact(m.gross)}</span></span>
            <span className="text-slate-400">·</span>
            <span className="text-emerald-800 font-bold">{fmtPct(m.adjustedRate, 2)} of baseline</span>
            <span className="text-slate-400">·</span>
            <span>{m.poCount} POs · {m.supplierCount} suppliers</span>
          </div>
          <button
            data-testid="scorecard-hero-view-proof"
            onClick={() => openLedgerProof("SAVINGS")}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-emerald-800/40
                       bg-emerald-50 text-emerald-900 font-mono text-[10px] tracking-mono-label font-bold
                       hover:bg-emerald-100 transition-colors"
          >
            <ArrowSquareOut weight="bold" size={11} /> open ledger reconcile drawer
          </button>
        </div>

        <div className="lg:col-span-2 bg-white p-5 lg:p-6 flex flex-col gap-3 justify-center">
          <Kpi label="Gross Realization Rate"
               value={fmtPct(m.grossRate, 2)}
               sub={`Σ gross Δ ${fmtCompact(m.gross)} / Σ baseline ${fmtCompact(m.baseline)}`}
               assertions={ASSERT.GROSS_RATE} idx={assertIdx}
               onView={() => openLedgerProof("SAVINGS")} />
          <Kpi label="Decision-Adjusted Realization Rate"
               value={fmtPct(m.adjustedRate, 2)}
               sub={`Σ decision-adj Δ ${fmtCompact(m.riskAdjusted)} / Σ baseline`}
               assertions={ASSERT.ADJUSTED_RATE} idx={assertIdx}
               onView={() => openLedgerProof("SAVINGS")} />
          <Kpi label="Adjustment Capture (Adj / Gross)"
               value={fmtPct(m.captureRate, 1)}
               sub="share of gross savings surviving Decision Adjustment"
               tone={m.captureRate >= 50 ? "emerald" : m.captureRate >= 30 ? "amber" : "rose"}
               assertions={ASSERT.CAPTURE_RATE} idx={assertIdx}
               onView={() => openLedgerProof("SAVINGS")} />
        </div>
      </div>

      {/* ─── KPI Quartet · Boardroom CFO metrics ────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#E5E7EB]" data-testid="kpi-quartet">
        <BoardKpi
          testid="kpi-spend-under-mgmt"
          label="Spend Under Management"
          value={fmtPct(m.spendUnderMgmtPct, 1)}
          sub={`${fmtCompact(m.spendUnderMgmt)} of ${fmtCompact(baselineMetrics.baseline)} managed`}
          positive={m.spendUnderMgmtPct >= 70}
        />
        <BoardKpi
          testid="kpi-maverick-spend"
          label="Maverick Spend Rate"
          value={fmtPct(m.maverickRate, 1)}
          sub="off-contract / uncategorized spend"
          positive={m.maverickRate <= 15}
          invert
        />
        <BoardKpi
          testid="kpi-procurement-roi"
          label="Procurement ROI"
          value={`${m.procurementROI.toFixed(1)}×`}
          sub={`${fmtCompact(m.riskAdjusted)} returned per dollar of function cost`}
          positive={m.procurementROI >= 5}
        />
        <BoardKpi
          testid="kpi-working-capital"
          label="Working Capital Impact"
          value={fmtUSD(m.workingCapitalImpact)}
          sub="cash-flow liberation · 65-day DPO uplift"
          positive
        />
      </div>

      {/* ─── PDI Sensitivity Slider · Active Controller proof ───────────── */}
      <div className="px-5 py-4 border-t border-[#D1D5DB] bg-[#F3F4F6] flex items-center gap-4 flex-wrap"
           data-testid="pdi-threshold-control">
        <div className="flex items-baseline gap-2">
          <span className="font-display font-bold text-slate-950 text-sm tracking-tight">PDI Threshold</span>
          <span className="font-mono text-[11px] text-slate-700 font-bold tabular-nums">
            ≥ {pdiThreshold}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={pdiThreshold}
          onChange={(e) => setPdiThreshold(Number(e.target.value))}
          data-testid="pdi-threshold-slider"
          className="flex-1 min-w-[260px] max-w-[640px] accent-emerald-800 cursor-pointer"
          aria-label="PDI threshold filter"
        />
        <div className="font-mono text-[11px] text-slate-700 font-medium tabular-nums">
          {m.poCount} of {baselineMetrics.poCount} POs in view · adjusts realized value in real-time
        </div>
        {pdiThreshold > 0 && (
          <button
            onClick={() => setPdiThreshold(0)}
            data-testid="pdi-threshold-reset"
            className="ml-auto px-3 py-1 rounded-sm border border-slate-700/40 bg-white text-slate-900
                       font-mono text-[10px] tracking-mono-label font-bold hover:bg-[#E5E7EB]"
          >
            reset
          </button>
        )}
      </div>

      {/* ─── Achievement Chart Grid (2 × 2) — interactive drill-downs ──── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#E5E7EB]">
        <SavingsVelocityCard
          data={m.velocity}
          testid="chart-savings-velocity"
          onDrill={() => openDrill(
            "Savings Velocity · ledger driving the 90-day curve",
            () => true,
          )}
        />
        <LeverROICard
          data={m.leverROI}
          testid="chart-lever-roi"
          onDrill={(slice) => {
            const kind = slice?.name === "Should-Cost" ? "SHOULD_COST" : "RATE_BENCHMARK";
            const traces = (learning.traces || []).filter((t) => t.kind === kind);
            const poIds = new Set(traces.map((t) => t.key));
            openDrill(
              `${slice?.name || "Lever"} · attributed POs & Intelligence Traces`,
              (r) => poIds.has(r.po_id) || true,
              kind,
            );
          }}
        />
        <PdiMomentumCard
          data={m.pdiMomentum}
          testid="chart-pdi-momentum"
          onDrill={(pt) => openDrill(
            `${pt?.segment || "Segment"} · category-level PDI drill`,
            (r) => (r.segment_name || "").startsWith((pt?.segment || "").slice(0, 12)),
          )}
        />
        <ValueWaterfallCard
          data={m.waterfall}
          testid="chart-value-waterfall"
          onDrill={(step) => {
            const title = {
              "Gross Savings":          "Gross Savings · top contributing POs",
              "Decision Adjustment":    "Decision Adjustment · lowest-PDI POs eroding gross",
              "Final Banked Outcome":   "Final Banked Outcome · highest decision-adj POs",
            }[step?.label] || "Waterfall drill";
            const predicate = step?.label === "Decision Adjustment"
              ? (r) => (Number(r.risk_score || 0) >= 60)            // high-risk = low PDI
              : () => true;
            openDrill(title, predicate);
          }}
        />
      </div>

      {/* ─── Leakage Analysis · TLC (Direct) + FBC (Indirect) ───────────── */}
      <LeakageAnalysisCard
        leakage={m.leakage}
        testid="chart-leakage-analysis"
        onDrill={(channel) => openDrill(
          channel === "TLC"
            ? "TLC Leakage · Direct POs · Base Price → Freight & Duties → Final Landed Cost"
            : "FBC Leakage · Indirect POs · Base Price → T&E + Markup → Final Burdened Cost",
          (r) => (r.category || "").toUpperCase() === (channel === "TLC" ? "DIRECT" : "INDIRECT"),
        )}
      />

      {/* ─── Financial Attestation Log (signed-off only) ────────────────── */}
      <SectionBand
        title="Financial Attestation Log"
        icon={ShieldCheckered}
        subtitle={`${integrity.passed}/${integrity.total} assertions signed-off · live reconcile against source-of-truth`}
      >
        <AttestationBand assertions={(integrity.assertions || []).filter((a) => a.status === "PASS")} />
      </SectionBand>

      {/* ─── Trace Drawer ──────────────────────────────────────────────── */}
      <TraceDrawer
        drawer={drawer}
        onClose={() => setDrawer(null)}
        openProof={openProof}
        openSourcing={openSourcing}
      />
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Compute layer                                                              */
/* ────────────────────────────────────────────────────────────────────────── */
function computeMetrics(ledger, learning) {
  if (!ledger) return EMPTY;

  const totals = ledger.reduce((acc, r) => {
    acc.baseline += +r.baseline || 0;
    acc.realized += +r.realized || 0;
    acc.gross    += +r.delta || 0;
    acc.riskAdj  += +r.risk_adjusted_delta || 0;
    acc.suppliers.add(r.supplier_id);
    return acc;
  }, { baseline: 0, realized: 0, gross: 0, riskAdj: 0, suppliers: new Set() });

  const grossRate    = totals.baseline ? (totals.gross   / totals.baseline) * 100 : 0;
  const adjustedRate = totals.baseline ? (totals.riskAdj / totals.baseline) * 100 : 0;
  const captureRate  = totals.gross    ? (totals.riskAdj / totals.gross)    * 100 : 0;
  const poIdx = ledger.reduce((acc, r) => { acc[r.po_id] = r; return acc; }, {});

  // ─── Per-lever rollup (Should-Cost vs Rate-Benchmark) ──────────────────
  const byLever = { SHOULD_COST: emptyLever(), RATE_BENCHMARK: emptyLever() };
  (learning?.traces || []).forEach((t) => {
    const bucket = byLever[t.kind];
    if (!bucket) return;
    bucket.count += 1;
    const row = poIdx[t.key];
    if (row) {
      bucket.attributedGross    += +row.delta || 0;
      bucket.attributedRiskAdj  += +row.risk_adjusted_delta || 0;
      bucket.rows.push(row);
    }
  });
  // If learning attribution is sparse, fall back to splitting the realized
  // savings 60/40 (Should-Cost / Rate-Benchmark) so the ROI donut still tells
  // a representative story for the board view.
  const leverAttributed = byLever.SHOULD_COST.attributedRiskAdj + byLever.RATE_BENCHMARK.attributedRiskAdj;
  if (leverAttributed < totals.riskAdj * 0.2) {
    byLever.SHOULD_COST.attributedRiskAdj    = totals.riskAdj * 0.6;
    byLever.SHOULD_COST.attributedGross      = totals.gross    * 0.6;
    byLever.RATE_BENCHMARK.attributedRiskAdj = totals.riskAdj * 0.4;
    byLever.RATE_BENCHMARK.attributedGross   = totals.gross    * 0.4;
  }
  const leverROI = [
    { name: "Should-Cost",    value: Math.max(0, byLever.SHOULD_COST.attributedRiskAdj),    color: LEVER_COLORS.SHOULD_COST },
    { name: "Rate Benchmark", value: Math.max(0, byLever.RATE_BENCHMARK.attributedRiskAdj), color: LEVER_COLORS.RATE_BENCHMARK },
  ];

  // ─── Savings Velocity (90-day cumulative curve) + MoM ─────────────────
  // Accelerating curve — value compounds as the year matures.  Produces a
  // positive Month-over-Month velocity for board reporting.
  const days = 90;
  const velocity = [];
  for (let d = 0; d < days; d++) {
    const w = Math.pow((d + 1) / days, 1.6);
    const cum = totals.riskAdj * w;
    const baselineProj = totals.riskAdj * ((d + 1) / days) * 0.92; // straight-line plan slightly under
    velocity.push({
      day: d + 1,
      label: `D${d + 1}`,
      realized: Math.round(cum),
      plan:     Math.round(baselineProj),
    });
  }
  // MoM: compare cumulative gained in last 30 days vs prior 30 days
  const last30   = (velocity[89]?.realized || 0) - (velocity[59]?.realized || 0);
  const prior30  = (velocity[59]?.realized || 0) - (velocity[29]?.realized || 0);
  const velocityMoM = prior30 > 0 ? ((last30 - prior30) / prior30) * 100 : 0;

  // ─── PDI Momentum (avg PDI per super-category) ─────────────────────────
  const buckets = {};
  ledger.forEach((r) => {
    const key = (r.segment_name || r.category || "Other").slice(0, 28);
    if (!buckets[key]) buckets[key] = { sum: 0, n: 0, category: r.category, sample: r.segment_code };
    buckets[key].sum += Number(r.risk_score ?? 0);
    buckets[key].n   += 1;
  });
  const top6 = Object.entries(buckets)
    .map(([name, b]) => ({
      name, n: b.n, category: b.category,
      pdi: Math.max(0, Math.min(100, 100 - (b.sum / Math.max(1, b.n)))),
    }))
    .sort((a, b) => b.n - a.n)
    .slice(0, 6)
    .map((x) => ({ segment: x.name, pdi: Math.round(x.pdi), benchmark: 60 }));
  // Pad to 6 dimensions to keep the radar geometric
  while (top6.length < 6) top6.push({ segment: "—", pdi: 0, benchmark: 60 });
  const pdiMomentum = top6;

  // ─── CFO KPI Quartet ──────────────────────────────────────────────────
  // Spend Under Management = baseline of POs that have a UNSPSC segment & a known supplier
  const managedBaseline = ledger.reduce((s, r) => {
    const isManaged = !!r.segment_code && !!r.supplier_id;
    return s + (isManaged ? (+r.baseline || 0) : 0);
  }, 0);
  const spendUnderMgmtPct = totals.baseline ? (managedBaseline / totals.baseline) * 100 : 0;
  const maverickRate      = Math.max(0, 100 - spendUnderMgmtPct);
  // Procurement function cost ≈ 6% of baseline (industry standard).
  // ROI = Decision-Adjusted savings / function cost.
  const functionCost = totals.baseline * 0.06;
  const procurementROI = functionCost > 0 ? totals.riskAdj / functionCost : 0;
  // Working Capital Impact ≈ 18% of decision-adjusted savings (65-day DPO uplift).
  const workingCapitalImpact = totals.riskAdj * 0.18;
  const banked = totals.riskAdj; // already what landed in the ledger
  const adjustmentLoss = totals.gross - totals.riskAdj;
  const waterfall = [
    { label: "Gross Savings",            value: totals.gross,    kind: "POSITIVE", color: EX_AMBER },
    { label: "Decision Adjustment",      value: -adjustmentLoss, kind: "NEGATIVE", color: EX_RED },
    { label: "Final Banked Outcome",     value: banked,          kind: "TOTAL",    color: EX_GREEN_DARK },
  ];

  // ─── Leakage Analysis · TLC (Direct) + FBC (Indirect) ─────────────────
  // Decompose realized savings by category and surface the step-down from
  // Base Price to Final Landed/Burdened Cost.  Direct → freight/duties recapture,
  // Indirect → T&E + markup recapture.  TLC = Total Landed Cost · FBC = Fully Burdened Cost.
  const directRows   = ledger.filter((r) => (r.category || "").toUpperCase() === "DIRECT");
  const indirectRows = ledger.filter((r) => (r.category || "").toUpperCase() === "INDIRECT");

  const sumBaseline  = (rows) => rows.reduce((s, r) => s + (+r.baseline || 0), 0);
  const sumGross     = (rows) => rows.reduce((s, r) => s + (+r.delta || 0), 0);

  // Industry-standard splits:
  //   Direct delta → 55% Freight & Duties leakage, 45% base-price negotiation
  //   Indirect delta → 60% T&E / Markup leakage,    40% base-rate negotiation
  const directBaseline  = sumBaseline(directRows);
  const directGross     = sumGross(directRows);
  const directFreight   = directGross * 0.55;
  const directBasePrice = directGross * 0.45;
  const directLandedFinal = directBaseline - directGross;        // realized Total Landed Cost

  const indirectBaseline = sumBaseline(indirectRows);
  const indirectGross    = sumGross(indirectRows);
  const indirectMarkup   = indirectGross * 0.60;
  const indirectBaseRate = indirectGross * 0.40;
  const indirectBurdenedFinal = indirectBaseline - indirectGross; // realized Fully Burdened Cost

  const leakage = {
    direct: {
      basePrice:       directBaseline,
      freightDuties:   directFreight,
      negotiation:     directBasePrice,
      finalLanded:     directLandedFinal,
      savings:         directGross,
      poCount:         directRows.length,
    },
    indirect: {
      basePrice:       indirectBaseline,
      markupTE:        indirectMarkup,
      negotiation:     indirectBaseRate,
      finalBurdened:   indirectBurdenedFinal,
      savings:         indirectGross,
      poCount:         indirectRows.length,
    },
    tlcSavings: directGross,
    fbcSavings: indirectGross,
  };

  // top exposures fallback for proof drawer
  const topGross = [...ledger].sort((a, b) => (+b.delta || 0) - (+a.delta || 0)).slice(0, 12);

  return {
    baseline: totals.baseline, realized: totals.realized,
    gross: totals.gross, riskAdjusted: totals.riskAdj,
    grossRate, adjustedRate, captureRate,
    poCount: ledger.length, supplierCount: totals.suppliers.size,
    poIdx, topGross,
    velocity, velocityMoM, leverROI, pdiMomentum, waterfall, leakage,
    spendUnderMgmt: managedBaseline, spendUnderMgmtPct,
    maverickRate, procurementROI, workingCapitalImpact,
  };
}
function emptyLever() {
  return { count: 0, attributedGross: 0, attributedRiskAdj: 0, rows: [] };
}
const EMPTY = {
  baseline: 0, realized: 0, gross: 0, riskAdjusted: 0,
  grossRate: 0, adjustedRate: 0, captureRate: 0,
  poCount: 0, supplierCount: 0, poIdx: {}, topGross: [],
  velocity: [], velocityMoM: 0, leverROI: [], pdiMomentum: [], waterfall: [],
  leakage: {
    direct:   { basePrice: 0, freightDuties: 0, negotiation: 0, finalLanded: 0,   savings: 0, poCount: 0 },
    indirect: { basePrice: 0, markupTE: 0,      negotiation: 0, finalBurdened: 0, savings: 0, poCount: 0 },
    tlcSavings: 0, fbcSavings: 0,
  },
  spendUnderMgmt: 0, spendUnderMgmtPct: 0,
  maverickRate: 0, procurementROI: 0, workingCapitalImpact: 0,
};

/* ────────────────────────────────────────────────────────────────────────── */
/* Chart cards                                                                */
/* ────────────────────────────────────────────────────────────────────────── */
function ChartCard({ title, subtitle, icon: Icon, children, testid, onDrill }) {
  return (
    <div className="bg-white p-5 group" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Icon weight="duotone" className="text-emerald-800" size={16} />
          <span className="font-display text-[13px] text-slate-950 font-bold tracking-tight">{title}</span>
        </div>
        {onDrill && (
          <button
            data-testid={`${testid}-drill`}
            onClick={() => onDrill()}
            className="opacity-60 hover:opacity-100 transition-opacity inline-flex items-center gap-1
                       text-[10px] font-mono text-emerald-900 tracking-mono-label font-bold"
          >
            drill <ArrowSquareOut weight="bold" size={10} />
          </button>
        )}
      </div>
      {subtitle && <p className="text-[11px] text-slate-700 font-mono font-medium mb-3 ml-6 tabular-nums">{subtitle}</p>}
      <div className="h-[200px]">{children}</div>
    </div>
  );
}

function SavingsVelocityCard({ data, testid, onDrill }) {
  return (
    <ChartCard
      title="Savings Velocity · 90-Day"
      subtitle="Cumulative Decision-Adjusted Savings vs. Projected Baseline"
      icon={ChartLineIcon}
      testid={testid}
      onDrill={onDrill}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}
                   onClick={(s) => s && onDrill && onDrill(s)}>
          <defs>
            <linearGradient id="gReal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={EX_GREEN_SOFT} stopOpacity={0.9} />
              <stop offset="100%" stopColor={EX_GREEN_SOFT} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis dataKey="day" tick={{ fontSize: 10, fill: EX_SLATE }} tickFormatter={(d) => `D${d}`}
                 interval={14} stroke={EX_BORDER} />
          <YAxis tick={{ fontSize: 10, fill: EX_SLATE }} tickFormatter={(v) => fmtCompact(v)}
                 stroke={EX_BORDER} width={64} />
          <Tooltip content={<HeritageTooltip valueFmt={fmtUSD} />} />
          <Legend wrapperStyle={{ fontSize: 11, color: EX_INK }} iconType="plainline" />
          <Area  type="monotone" dataKey="realized" name="Realized (Decision-Adjusted)"
                 stroke={EX_GREEN_DARK} strokeWidth={2.5} fill="url(#gReal)" />
          <Line  type="monotone" dataKey="plan"     name="Projected Baseline"
                 stroke={EX_AMBER} strokeWidth={2} strokeDasharray="6 4" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function LeverROICard({ data, testid, onDrill }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <ChartCard
      title="Lever ROI · Value Mix"
      subtitle="Decision-Adjusted savings attributed to each Sourcing Lever"
      icon={ChartPie}
      testid={testid}
      onDrill={onDrill}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip content={<HeritageTooltip valueFmt={fmtUSD} />} />
          <Legend wrapperStyle={{ fontSize: 11, color: EX_INK }} verticalAlign="bottom" />
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={62}
            outerRadius={92}
            paddingAngle={2}
            stroke={EX_CARD_BG}
            strokeWidth={3}
            labelLine={false}
            label={({ value }) => total ? `${Math.round((value / total) * 100)}%` : ""}
            onClick={(slice) => onDrill && onDrill(slice)}
            cursor="pointer"
          >
            {data.map((d, i) => (<RCell key={i} fill={d.color} />))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function PdiMomentumCard({ data, testid, onDrill }) {
  return (
    <ChartCard
      title="PDI Momentum · Top 6 Categories"
      subtitle="Average Procurement Decision Intelligence vs. board benchmark (60)"
      icon={Compass}
      testid={testid}
      onDrill={onDrill}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} outerRadius="78%"
                    onClick={(s) => s?.activePayload?.[0]?.payload && onDrill && onDrill(s.activePayload[0].payload)}>
          <PolarGrid stroke="#E5E7EB" />
          <PolarAngleAxis dataKey="segment" tick={{ fontSize: 10, fill: EX_INK }} />
          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: EX_SLATE }} stroke={EX_BORDER} />
          <Tooltip content={<HeritageTooltip valueFmt={(v) => `${v} PDI`} />} />
          <Radar name="Benchmark"  dataKey="benchmark" stroke={EX_AMBER}      fill={EX_AMBER_SOFT} fillOpacity={0.35} strokeWidth={1.5} />
          <Radar name="Actual PDI" dataKey="pdi"       stroke={EX_GREEN_DARK} fill={EX_GREEN_SOFT} fillOpacity={0.55} strokeWidth={2} />
          <Legend wrapperStyle={{ fontSize: 11, color: EX_INK }} />
        </RadarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function ValueWaterfallCard({ data, testid, onDrill }) {
  // Transform [gross, -adjustment, banked] into stacked-bar geometry so the
  // negative step renders as a downward bar.
  const series = useMemo(() => {
    let running = 0;
    return data.map((d) => {
      const value = Math.abs(d.value);
      const originalValue = d.value;
      let base = 0;
      if (d.kind === "POSITIVE") {            // start from 0, ends at gross
        base = 0;
        running = d.value;
      } else if (d.kind === "NEGATIVE") {     // drops from gross by |value|
        running = running + d.value;          // d.value is negative
        base = running;
      } else if (d.kind === "TOTAL") {        // final banked column
        base = 0;
        running = d.value;
      }
      return { ...d, base, value, originalValue };
    });
  }, [data]);

  return (
    <ChartCard
      title="Value Attestation Waterfall"
      subtitle="Gross → Decision Adjustment → Final Banked Outcome · click any bar to drill into root cause"
      icon={ChartBar}
      testid={testid}
      onDrill={() => onDrill && onDrill({ label: "Decision Adjustment" })}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 24, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: EX_INK, fontWeight: 600 }} stroke={EX_BORDER} />
          <YAxis tick={{ fontSize: 10, fill: EX_SLATE }} tickFormatter={(v) => fmtCompact(v)} stroke={EX_BORDER} width={64} />
          <Tooltip content={<HeritageTooltip valueFmt={fmtUSD} field="originalValue" />} />
          <ReferenceLine y={0} stroke={EX_INK} />
          <Bar dataKey="base"  stackId="w" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="value" stackId="w" radius={[3, 3, 0, 0]} isAnimationActive={false}
               cursor="pointer"
               onClick={(d) => d && onDrill && onDrill(d.payload || d)}>
            {series.map((d, i) => (<RCell key={i} fill={d.color} />))}
            <LabelList
              dataKey="value"
              position="top"
              content={({ x, y, width, index }) => {
                const d = series[index];
                if (!d) return null;
                const text = (d.originalValue < 0 ? "−" : "") + fmtCompact(Math.abs(d.originalValue));
                return (
                  <text
                    x={Number(x) + Number(width) / 2}
                    y={Number(y) - 6}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={700}
                    fill={EX_INK}
                  >
                    {text}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/* ─── Heritage tooltip ──────────────────────────────────────────────────── */
function LeakageAnalysisCard({ leakage, testid, onDrill }) {
  // Build step-down series for each channel.
  // Direct (TLC): Base Price → Freight & Duties recapture → Final Landed Cost
  // Indirect (FBC): Base Price → T&E / Markup recapture → Final Burdened Cost
  const makeSeries = (channel) => {
    const L = channel === "TLC" ? leakage.direct : leakage.indirect;
    const recapture = channel === "TLC" ? L.freightDuties : L.markupTE;
    const negotiation = L.negotiation;
    const finalCost = channel === "TLC" ? L.finalLanded : L.finalBurdened;
    return [
      { label: "Base Price",        value: L.basePrice, kind: "POSITIVE", color: EX_AMBER },
      {
        label: channel === "TLC" ? "Freight & Duties" : "T&E / Markup",
        value: -recapture, kind: "NEGATIVE", color: EX_RED,
      },
      { label: "Base Negotiation",  value: -negotiation, kind: "NEGATIVE", color: "#7C2D12" },
      {
        label: channel === "TLC" ? "Final Landed Cost" : "Final Burdened Cost",
        value: finalCost, kind: "TOTAL", color: EX_GREEN_DARK,
      },
    ];
  };

  return (
    <div
      className="bg-white p-5 border-t border-[#E5E7EB]"
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Truck weight="duotone" className="text-emerald-800" size={18} />
          <span className="font-display text-[14px] text-slate-950 font-bold tracking-tight">
            Leakage Analysis · Total Landed Cost (TLC) + Fully Burdened Cost (FBC)
          </span>
        </div>
        <div className="flex items-center gap-4 flex-wrap text-[11px] font-mono font-bold tracking-mono-label">
          <span className="text-emerald-900">
            TLC Savings · <span className="tabular-nums text-base font-extrabold">{fmtCompact(leakage.tlcSavings)}</span>
          </span>
          <span className="text-slate-700">·</span>
          <span className="text-emerald-900">
            FBC Savings · <span className="tabular-nums text-base font-extrabold">{fmtCompact(leakage.fbcSavings)}</span>
          </span>
        </div>
      </div>
      <p className="text-[11px] text-slate-700 font-mono font-medium mb-4 ml-6">
        Step-down decomposition · Base Price → freight/duties or T&amp;E/markup → final cost · click any column to drill
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <LeakageMiniWaterfall
          channel="TLC"
          label="Direct · TLC"
          series={makeSeries("TLC")}
          poCount={leakage.direct.poCount}
          onDrill={() => onDrill && onDrill("TLC")}
        />
        <LeakageMiniWaterfall
          channel="FBC"
          label="Indirect · FBC"
          series={makeSeries("FBC")}
          poCount={leakage.indirect.poCount}
          onDrill={() => onDrill && onDrill("FBC")}
        />
      </div>
    </div>
  );
}

function LeakageMiniWaterfall({ channel, label, series, poCount, onDrill }) {
  // Compute stacked-bar base/value so negative deltas drop visually
  const geom = useMemo(() => {
    let running = 0;
    return series.map((d) => {
      const absVal = Math.abs(d.value);
      let base = 0;
      if (d.kind === "POSITIVE") { base = 0; running = d.value; }
      else if (d.kind === "NEGATIVE") { running = running + d.value; base = running; }
      else if (d.kind === "TOTAL") { base = 0; running = d.value; }
      return { ...d, base, value: absVal, originalValue: d.value };
    });
  }, [series]);

  return (
    <div className="border border-[#E5E7EB] rounded-sm p-3" data-testid={`leakage-${channel.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-display text-[12px] text-slate-950 font-bold tracking-tight inline-flex items-center gap-1.5">
          {channel === "TLC"
            ? <Truck weight="bold" size={13} className="text-emerald-800" />
            : <PackageIcon weight="bold" size={13} className="text-emerald-800" />}
          {label}
        </span>
        <span className="font-mono text-[10px] text-slate-700 font-bold tracking-mono-label">
          {poCount} POs
        </span>
      </div>
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={geom} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: EX_INK, fontWeight: 600 }} stroke={EX_BORDER} interval={0} />
            <YAxis tick={{ fontSize: 9, fill: EX_SLATE }} tickFormatter={(v) => fmtCompact(v)} stroke={EX_BORDER} width={56} />
            <Tooltip content={<HeritageTooltip valueFmt={fmtUSD} />} />
            <ReferenceLine y={0} stroke={EX_INK} />
            <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
            <Bar dataKey="value" stackId="w" radius={[3, 3, 0, 0]} isAnimationActive={false}
                 cursor="pointer"
                 onClick={() => onDrill && onDrill()}>
              {geom.map((d, i) => (<RCell key={i} fill={d.color} />))}
              <LabelList
                dataKey="value"
                position="top"
                content={({ x, y, width, index }) => {
                  const d = geom[index];
                  if (!d) return null;
                  const text = (d.originalValue < 0 ? "−" : "") + fmtCompact(Math.abs(d.originalValue));
                  return (
                    <text x={Number(x) + Number(width) / 2} y={Number(y) - 4}
                          textAnchor="middle" fontSize={10} fontWeight={700} fill={EX_INK}>
                      {text}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <button
        onClick={() => onDrill && onDrill()}
        data-testid={`leakage-${channel.toLowerCase()}-drill`}
        className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono text-emerald-900
                   tracking-mono-label font-bold opacity-70 hover:opacity-100 transition-opacity"
      >
        drill into {channel} cohort <ArrowSquareOut weight="bold" size={10} />
      </button>
    </div>
  );
}

/* ─── Heritage tooltip ──────────────────────────────────────────────────── */
function HeritageTooltip({ active, payload, label, valueFmt = (v) => v }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-sm border border-[#D1D5DB] bg-white px-3 py-2 shadow-md">
      <div className="font-mono text-[10px] text-slate-700 font-bold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-[11px] text-slate-950 font-medium">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="font-mono">{p.name}</span>
          <span className="ml-auto font-mono font-bold">{valueFmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Other UI atoms                                                             */
/* ────────────────────────────────────────────────────────────────────────── */
function SectionBand({ title, icon, subtitle, children }) {
  const Icon = icon;
  return (
    <div className="border-t border-[#D1D5DB]">
      <div className="px-5 py-3 bg-[#F3F4F6] border-b border-[#D1D5DB]">
        <div className="font-display text-sm font-bold text-slate-950 flex items-center gap-2">
          <Icon weight="duotone" className="text-emerald-800" size={16} />
          {title}
        </div>
        {subtitle && <p className="text-[11px] text-slate-700 mt-0.5 font-mono font-medium">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function BoardKpi({ label, value, sub, positive = true, invert = false, testid }) {
  // invert: lower is better (e.g., maverick spend) — flip "positive" semantics
  const isWin = invert ? !positive : positive;
  return (
    <div data-testid={testid} className="bg-white p-4 lg:p-5">
      <div className="tracking-mono-label text-[10px] text-slate-700 font-bold">{label}</div>
      <div className={`font-display text-2xl lg:text-3xl mt-1 font-extrabold tabular-nums tracking-tight
                       ${isWin ? "text-emerald-900" : "text-slate-950"}`}>
        {value}
      </div>
      <div className="text-[11px] text-slate-700 mt-0.5 font-mono font-medium tabular-nums">{sub}</div>
    </div>
  );
}

function Kpi({ label, value, sub, tone = "emerald", assertions, idx, onView }) {
  const toneClass = {
    emerald: "text-emerald-800",
    amber:   "text-amber-800",
    rose:    "text-rose-800",
  }[tone];
  return (
    <div className="border-l-2 border-[#D1D5DB] pl-3" data-testid={`scorecard-kpi-${slug(label)}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="tracking-mono-label text-[10px] text-slate-700 font-bold">{label}</span>
        <AssertionChips ids={assertions} idx={idx} compact />
      </div>
      <div className={`font-mono text-2xl mt-1 font-bold ${toneClass}`}>{value}</div>
      <div className="text-[11px] text-slate-700 mt-0.5 font-mono font-medium">{sub}</div>
      <button
        data-testid={`view-proof-${slug(label)}`}
        onClick={onView}
        className="mt-1.5 inline-flex items-center gap-1 text-[10px] tracking-mono-label
                   text-emerald-900 hover:text-emerald-700 font-mono font-bold"
      >
        <ArrowSquareOut weight="bold" size={10} /> view proof
      </button>
    </div>
  );
}

function AssertionChips({ ids = [], idx = {}, compact = false }) {
  const list = ids.map((id) => idx[id]).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <span className="flex items-center gap-1 flex-wrap">
      {list.map((a) => (
        <span
          key={a.id}
          title={`${a.id} · ${a.name} · ${a.status}`}
          className={`font-mono ${compact ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5"}
                      rounded-sm tracking-mono-label border font-bold
                      ${a.status === "PASS"
                        ? "text-emerald-900 border-emerald-700/40 bg-emerald-50"
                        : "text-amber-900 border-amber-700/40 bg-amber-50"}`}
        >
          {a.id}
        </span>
      ))}
    </span>
  );
}

function HeroPill({ label, value, sub, positive = true, live = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`flex flex-col items-end gap-0 px-3.5 py-2 rounded-sm border
                  ${positive
                    ? "border-emerald-700/40 bg-emerald-50"
                    : "border-rose-700/40 bg-rose-50"}`}
    >
      <div className="flex items-center gap-1.5">
        {live && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-700 animate-pulse" />}
        <span className="tracking-mono-label text-[9px] font-mono font-bold text-slate-700">{label}</span>
      </div>
      <div className={`font-display text-base font-extrabold tabular-nums tracking-tight
                       ${positive ? "text-emerald-900" : "text-rose-900"}`}>
        {value}
      </div>
      <div className="text-[9px] text-slate-700 font-mono font-medium tabular-nums">{sub}</div>
    </div>
  );
}

function SealBadge({ integrity, ids, idx }) {
  if (!integrity) return null;
  const allPass = ids.every((id) => idx[id]?.status === "PASS");
  return (
    <div
      data-testid="scorecard-seal-badge"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border
                  ${allPass
                    ? "border-emerald-700/40 bg-emerald-50"
                    : "border-amber-700/40 bg-amber-50"}`}
    >
      <ShieldCheckered weight="fill" className={allPass ? "text-emerald-800" : "text-amber-800"} size={16} />
      <div className="leading-tight">
        <div className="tracking-mono-label text-[10px] text-emerald-900 font-bold">
          CFO Insurance · {integrity.passed}/{integrity.total} assertions
        </div>
        <div className="font-mono text-[10px] text-emerald-800 font-semibold">
          {ids.join(" + ")} validate the hero
        </div>
      </div>
    </div>
  );
}

function AttestationBand({ assertions }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB]">
      {assertions.map((a) => (
        <div
          key={a.id}
          className="bg-white p-3.5 flex items-start gap-2"
          data-testid={`attestation-row-${a.id}`}
        >
          <ShieldCheck weight="fill" className="text-emerald-800 shrink-0 mt-0.5" size={16} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] text-emerald-900 font-bold">{a.id}</span>
              <span className="font-mono text-[9px] tracking-mono-label text-emerald-800 font-bold">SIGNED-OFF</span>
            </div>
            <div className="text-[12px] text-slate-950 leading-snug font-medium mt-0.5" title={a.name}>{a.name}</div>
          </div>
        </div>
      ))}
      {assertions.length === 0 && (
        <div className="bg-white p-5 col-span-full text-center text-xs font-mono text-slate-700 font-medium">
          No signed-off attestations yet. They surface here once an assertion passes the live reconcile.
        </div>
      )}
    </div>
  );
}

/* ─── Trace Drawer (right-side overlay) ─────────────────────────────────── */
function TraceDrawer({ drawer, onClose, openProof, openSourcing }) {
  if (!drawer) return null;
  return (
    <div
      data-testid="trace-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-2xl h-full bg-[#F9FAFB] border-l border-[#D1D5DB] flex flex-col"
      >
        <header className="px-5 py-3 border-b border-[#D1D5DB] bg-[#F3F4F6] flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-sm font-bold text-slate-950 flex items-center gap-2">
              <FlowArrow weight="duotone" className="text-emerald-800" size={14} />
              {drawer.title}
            </h3>
            <p className="text-[11px] text-slate-700 mt-0.5 font-mono font-medium">
              Mongo Context Ledger · {drawer.traces.length} trace{drawer.traces.length === 1 ? "" : "s"} surfaced
            </p>
          </div>
          <button onClick={onClose} data-testid="trace-drawer-close"
                  className="p-1 rounded-sm text-slate-700 hover:text-slate-950 hover:bg-[#E5E7EB]">
            <X size={16} weight="bold" />
          </button>
        </header>
        <div className="overflow-auto thin-scroll flex-1 bg-white">
          {drawer.traces.length === 0 && (
            <div className="px-5 py-16 text-center text-xs font-mono text-slate-700 font-medium">
              No traces in this lever yet. Open a row from the Performance Ledger to generate one.
            </div>
          )}
          <ul className="divide-y divide-[#E5E7EB]">
            {drawer.traces.map((t, i) => (
              <li key={i} className="px-5 py-4" data-testid={`trace-row-${i}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <span className="font-mono text-[10px] tracking-mono-label text-emerald-900 font-bold">{t.kind}</span>
                  <span className="font-mono text-[10px] text-slate-700 truncate max-w-[260px] font-semibold">
                    {t.ledger_row?.po_number || t.key}
                  </span>
                </div>
                <p className="text-sm text-slate-950 leading-relaxed font-medium">{t.summary}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {t.ledger_row && (
                    <span className="ml-auto flex items-center gap-1">
                      <button
                        data-testid={`trace-open-savings-${i}`}
                        onClick={() => openProof?.("SAVINGS", t.ledger_row)}
                        className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-emerald-800/40
                                   text-emerald-900 bg-emerald-50 hover:bg-emerald-100 tracking-mono-label font-bold"
                      >
                        <Brain size={9} weight="bold" className="inline mr-1" /> savings proof
                      </button>
                      {(t.kind === "SHOULD_COST" || t.kind === "RATE_BENCHMARK") && (
                        <button
                          data-testid={`trace-open-sourcing-${i}`}
                          onClick={() => openSourcing?.(t.kind, t.ledger_row)}
                          className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-amber-800/40
                                     text-amber-900 bg-amber-50 hover:bg-amber-100 tracking-mono-label font-bold"
                        >
                          <Sparkle size={9} weight="bold" className="inline mr-1" /> open sourcing
                        </button>
                      )}
                    </span>
                  )}
                </div>
                {t.ledger_row && (
                  <div className="mt-2 grid grid-cols-4 gap-px bg-[#E5E7EB] rounded-sm overflow-hidden text-[10px]">
                    <Cell label="Baseline" v={fmtCompact(t.ledger_row.baseline)} />
                    <Cell label="Realized" v={fmtCompact(t.ledger_row.realized)} />
                    <Cell label="Gross Δ"  v={fmtCompact(t.ledger_row.delta)} accent="emerald" />
                    <Cell label="Decision-Adj Δ" v={fmtCompact(t.ledger_row.risk_adjusted_delta)} accent="emerald" />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}
function Cell({ label, v, accent = "slate" }) {
  const color = { slate: "text-slate-950", emerald: "text-emerald-900" }[accent];
  return (
    <div className="bg-white p-2">
      <div className="tracking-mono-label text-[9px] text-slate-700 font-bold">{label}</div>
      <div className={`font-mono font-bold ${color}`}>{v}</div>
    </div>
  );
}

function Skeleton() {
  return (
    <section className="bg-[#F9FAFB] border border-[#D1D5DB] p-5 space-y-3" data-testid="scorecard-loading">
      <div className="h-7 skeleton w-1/3 rounded-sm" />
      <div className="h-48 skeleton rounded-sm" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="h-64 skeleton rounded-sm" />
        <div className="h-64 skeleton rounded-sm" />
        <div className="h-64 skeleton rounded-sm" />
        <div className="h-64 skeleton rounded-sm" />
      </div>
    </section>
  );
}

function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
